import { useCallback, useState } from "react";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useContratoPagination } from "../hooks/contrato/pagination";
import { useContratoFilters } from "../hooks/contrato/filter";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { processError } from "../../../../common/utils/base_error";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { useNavigate, useParams } from "react-router";
import { ContratoLista } from "../../domain/models/contrato_lista";

type LoadContratoFuncState = {
  IdContrato?: number;
  page?: number;
  rowsPerPage?: number;
  centroDeCusto?: number[],
  centroDeCustoLista?: CentroCustoDropDown[] | undefined,
  FornecedorPesquisa?: number,
  DocumentosPesquisa?: number,
  AssinadoPesquisa?: number,
  NDocumentoPesquisa?: string,
  StatusPesquisa?: number,
};

export const useContratoListaViewModel = () => {
  const {
    commons: { },
    terceirizacao: { getAllListagemContratoUseCase, deleteContratoUseCase },
  } = useDependencyInjection();
  const debouncedRef = useCreateDebounceHandler(750);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [contratoLista, setContratLista] = useState<ContratoLista[]>([]);
  const [selectedContrato, setSelectedContrato] = useState<ContratoLista>();
  const [contratoDeleteModal, setContratoDeleteModal] = useState<boolean>(false);
  const { pushErrorNotification, setCustomLoading, pushInfoNotification } = usePageLoader();
  const filters = useContratoFilters();
  const pagination = useContratoPagination();
  const navigate = useNavigate();
  const { d, s } = useParams();

  const {
    loadCentroCustoDropDown,
  } = useDropDownLoader();

  // #region Funções de carregamento
  const loadContratoListaState = useCallback(async (funcState?: LoadContratoFuncState) => {
    try {
      const filters = useContratoFilters.getState();
      const pagination = useContratoPagination.getState();

      if (d !== undefined && d !== null && d !== 'null') filters.DocumentosPesquisa = Number(d);
      if (s !== undefined && s !== null && s !== 'null') filters.StatusPesquisa = Number(s);

      const filterID = evaluateParamValue(funcState?.IdContrato, filters.IdContrato);
      const filterFornecedor = funcState?.FornecedorPesquisa ?? filters.FornecedorPesquisa;
      const filterStatusPesquisa = funcState?.StatusPesquisa ?? filters.StatusPesquisa;
      const filterDocumentosPesquisa = funcState?.DocumentosPesquisa ?? filters.DocumentosPesquisa;
      const filterNDocumentoPesquisa = funcState?.NDocumentoPesquisa ?? filters.NDocumentoPesquisa;
      const filterAssinadoPesquisa = funcState?.AssinadoPesquisa ?? filters.AssinadoPesquisa;
      const filterCentroDeCusto = funcState?.centroDeCusto ?? filters.centroDeCusto;
      const page = funcState?.page ?? pagination.page;
      const rows = funcState?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllListagemContratoUseCase.getAllListagemContrato({
        IdContrato: filterID,
        centroDeCusto: filterCentroDeCusto,
        DocumentosPesquisa: filterDocumentosPesquisa,
        FornecedorPesquisa: filterFornecedor,
        NDocumentoPesquisa: String(filterNDocumentoPesquisa),
        AssinadoPesquisa: filterAssinadoPesquisa,
        StatusPesquisa: filterStatusPesquisa,
      },
      page,
      rows
    );
    console.log(getAllListagemContratoUseCase.items)
      setContratLista(getAllListagemContratoUseCase.items);
      pagination.setRowsPerPage(getAllListagemContratoUseCase.pageSize);
      pagination.setTotalItems(getAllListagemContratoUseCase.totalItems);
    } catch (e: unknown) {
      throw new Error("Falha ao buscar contratos!");
    }
  }, [d, getAllListagemContratoUseCase, s]);

  //#endregion

  // #region useEffect

  usePageStartBootstrap(async () => {
    try {
      await loadContratoListaState();
      await loadCentroCustoDropDown(list => useContratoFilters.setState({ centroDeCustoLista: list }));
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados da pagina!')[0]);
    }
  });

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadContratoListaState();
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadContratoListaState, pushErrorNotification]);
  //#endregion

  // #region Eventos
  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      pagination.setPage(page + 1);;
      await loadContratoListaState({ page: page + 1, });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadContratoListaState, pagination, pushErrorNotification]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      pagination.setPage(1);
      pagination.setRowsPerPage(rowsPerPage);
      await loadContratoListaState({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadContratoListaState, pagination, pushErrorNotification]);

  const handleCadastroContratoOpenState = useCallback((IdContrato: number | null, openState: boolean) => {
    if (openState) {
      if (IdContrato) {
        navigate(`/Contrato_Cadastro/${IdContrato}`)
      }
    }
  }, [navigate]);

  //#endregion


  //#region Filtros
  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);
      filters.clearFilters();
      pagination.setPage(1);
      await loadContratoListaState();
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar Diário de Obras!'));
    } finally {
      setLoading(false);
    }
  }, [filters, loadContratoListaState, pagination, pushErrorNotification]);

  const handleFornecedorChange = useDebouncedCallback(debouncedRef, async (newValue: number) => {
    useContratoFilters.getState().setFornecedorPesquisa(newValue);

    try {
      setLoading(true);
      useContratoPagination.getState().setPage(1);
      await loadContratoListaState({ FornecedorPesquisa: newValue, page: 1 } as any);
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar Diario de Obras!"));
    } finally {
      setLoading(false);
    }

    return () => { };
  }, [pushErrorNotification, loadContratoListaState]);

  const handleCentroDeCustoChange = useDebouncedCallback(debouncedRef, (newValue: number[]) => {
    useContratoFilters.getState().setCentroDeCusto(newValue);
    return async () => {
      try {
        setLoading(true);
        useContratoPagination.getState().setPage(1);
        await loadContratoListaState({ centroDeCusto: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar Ações Preventivas!"));
      } finally {
        setLoading(false);
      }
    }
  }, [loadContratoListaState, pushErrorNotification,]);

  const handleDocumentosChange = useDebouncedCallback(debouncedRef, (newValue: number) => {
    useContratoFilters.getState().setDocumentosPesquisa(newValue);
    return async () => {
      try {
        setLoading(true);
        useContratoPagination.getState().setPage(1);
        await loadContratoListaState({ DocumentosPesquisa: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar Diario de Obras!"));
      } finally {
        setLoading(false);
      }
    }
  }, [loadContratoListaState, pushErrorNotification,]);

  const handleNDocumentoCampoChange = useDebouncedCallback(debouncedRef, (newValue: string) => {
    useContratoFilters.getState().setNDocumentoPesquisa(newValue);
    return async () => {
      try {
        setLoading(true);
        useContratoPagination.getState().setPage(1);
        await loadContratoListaState({ NDocumentoPesquisa: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar Diario de Obras!"));
      } finally {
        setLoading(false);
      }
    }
  }, [loadContratoListaState, pushErrorNotification,]);

  const handleAssinadoChange = useDebouncedCallback(debouncedRef, (newValue: number) => {
    useContratoFilters.getState().setAssinadoPesquisa(newValue);
    return async () => {
      try {
        setLoading(true);
        useContratoPagination.getState().setPage(1);
        await loadContratoListaState({ AssinadoPesquisa: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar Diario de Obras!"));
      } finally {
        setLoading(false);
      }
    }
  }, [loadContratoListaState, pushErrorNotification,]);

  const handleStatusPesquisaChange = useDebouncedCallback(debouncedRef, (newValue: number) => {
    useContratoFilters.getState().setStatusPesquisa(newValue);
    return async () => {
      try {
        setLoading(true);
        useContratoPagination.getState().setPage(1);
        await loadContratoListaState({ StatusPesquisa: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar Diario de Obras!"));
      } finally {
        setLoading(false);
      }
    }
  }, [loadContratoListaState, pushErrorNotification,]);
  //#endregion

  //#region Funções de deletar
  const handleDeleteContratoModalOpenState = useCallback((IdContrato: number | null, openState: boolean) => {
    if (openState) {
      if (IdContrato && contratoLista) {
        setSelectedContrato(contratoLista.find(x => x.IdContrato === IdContrato));
      } else {
        setSelectedContrato(undefined);
      }
    }
    setContratoDeleteModal(openState);
  }, [contratoLista]);

  const handleDeleteConfirmaContrato = useCallback(async () => {
    if (selectedContrato) {
      try {
        setSaving(true);
        await deleteContratoUseCase.deleteContrato(selectedContrato.IdContrato);
        await loadContratoListaState();
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao excluir informações!'));
      } finally {
        setSaving(false);
        setContratoDeleteModal(false);
      }
    }
  }, [selectedContrato, deleteContratoUseCase, loadContratoListaState, pushErrorNotification]);

  //#endregion



  return {
    loading,
    errorMessage,
    saving,
    pagination,
    filters,
    contratoLista,
    selectedContrato,
    contratoDeleteModal,
    handlePageChange,
    handleRowsPerPageChange,
    handleReload,
    handleClearFilters,
    handleFornecedorChange,
    handleCentroDeCustoChange,
    handleDocumentosChange,
    handleNDocumentoCampoChange,
    handleStatusPesquisaChange,
    handleDeleteConfirmaContrato,
    handleDeleteContratoModalOpenState,
    handleCadastroContratoOpenState,
    handleAssinadoChange,
  };
};
